import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import { TextInput } from "../../components/forms";
import { Button, PageTitle, Section, Loading } from "../../components";
import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  SearchIcon,
} from "../../assets/icons";
import "./SuppliersPage.scss";
import useSupplierService from "../../services/api/supplierService";
import { useInfiniteQuery } from "@tanstack/react-query";
import { enums } from "../../constants/enum";

function SuppliersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const { getAllSuppliers, queryKey } = useSupplierService();
  const { ref, inView } = useInView();

  const { data, isLoading, error, isFetchingNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [queryKey, { searchQuery }],
      queryFn: ({ pageParam = 1 }) => getAllSuppliers(searchQuery, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const handleSearch = useCallback((query) => {
    if (!query) {
      setSearchQuery("");
    } else {
      setSearchQuery(query);
    }
    refetch();
  }, [refetch]);

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }
  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }
  return (
    <div className="page suppliers-page">
      <PageTitle text="Suppliers">
        <Link to="/suppliers/add">
          <Button text="Add New Supplier" icon={<AddIcon />} />
        </Link>
      </PageTitle>

      <Section title="All Suppliers" className="supplier-details">
        <div className="supplier-search">
          <Formik  enableReinitialize initialValues={{ query: searchQuery }} onSubmit={({query})=>handleSearch(query)}>
            <Form>
              <TextInput
                name="query"
                type="search"
                label="Search by supplier name"
                placeholder="Enter supplier name"
              />
              <div className="form-actions">
                <Button text="Search" icon={<SearchIcon />} type="submit" />
                <Button
                  type="reset"
                  icon={<CloseIcon />}
                  text="Clear"
                  outlined
                  className="clear-btn"
                  onClick={() => handleSearch("")}
                />
              </div>
            </Form>
          </Formik>
        </div>
        <div className="supplier-list">
          <div className="table-title">
            <h3>Suppliers</h3>
          </div>
          <div className="table-content">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile Number</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.pages.map(({ suppliers }) =>
                  suppliers.map((supplier) => (
                    <tr key={supplier._id}>
                      <td>
                        {supplier?.name
                          ? supplier?.name
                          : supplier?.companyName}
                      </td>
                      <td>{supplier?.phoneNumbers?.[0]}</td>
                      <td>
                        <Link to={`/suppliers/details/${supplier?._id}`}>
                          <Button
                            text="View"
                            prefixIcon={<ArrowLongRightIcon />}
                          />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              ref={ref}
            >
              {isFetchingNextPage ? <Loading variant="inline" /> : null}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default SuppliersPage;
